import React, { useEffect } from 'react';
import { StaticRouter, StaticRouterProps, useHistory, useLocation, Switch, SwitchProps, Route, RouteProps, RouteComponentProps } from 'react-router';
import { BrowserRouter, BrowserRouterProps } from 'react-router-dom';
import IRouteConfig, { IRouteConfigItem } from './IRouteConfig.js';
import IEpiserverContext from '../Core/IEpiserverContext.js';
import { useEpiserver } from '../Hooks/Context.js';

export type RouterProps = StaticRouterProps & BrowserRouterProps;
export const Router: React.FunctionComponent<RouterProps> = (props) => {
    const epi = useEpiserver();

    if (epi.isServerSideRendering()) {
        const staticRouterProps: StaticRouterProps = {
            basename: props.basename,
            context: props.context,
            location: props.location
        };
        return <StaticRouter {...staticRouterProps}>{props.children}</StaticRouter>
    }

    const browserRouterProps: BrowserRouterProps = {
        basename: props.basename,
        forceRefresh: props.forceRefresh,
        getUserConfirmation: props.getUserConfirmation,
        keyLength: props.keyLength
    };

    if (epi.isInEditMode() || epi.isEditable())
        return <BrowserRouter {...browserRouterProps}>{props.children}</BrowserRouter>

    return <BrowserRouter {...browserRouterProps}><ElementNavigation>{props.children}</ElementNavigation></BrowserRouter>
}
Router.displayName = "Optimizely CMS: Router";
export default Router;

const ElementNavigation: React.FunctionComponent = (props): React.ReactElement => {
    const history = useHistory();
    const location = useLocation();
    const epi = useEpiserver();
    const config = epi.config();

    useEffect(() => {
        if (epi.isInEditMode() || epi.isServerSideRendering()) {
            if (epi.isDebugActive()) console.info('ElementNavigation: Edit mode, or SSR, so not attaching events');
            return;
        } else {
            if (epi.isDebugActive()) console.info('ElementNavigation: Enabling catch-all click handling for navigation');
        }

        const onWindowClick = (event: MouseEvent) => {
            const target: HTMLElement = (event.target as any) as HTMLElement;
            const currentUrl: URL = new URL(window.location.href);
            let newPath = '';
            let isBlob = false;
            const currentUrlLang = currentUrl.pathname.indexOf('/fi/') !== -1 || currentUrl.pathname.endsWith('/fi') ? 'fi' : 'en'
            let targetUrlString = ''
            // Custom logic for Bootstrap Tabs and Country map click to update search parameters when click.
            let updateSearchParams = target.getAttribute('data-update-search-params');

            const svgNodeTypes = ['svg', 'g', 'path', 'circle']
            if (svgNodeTypes.indexOf(target.nodeName.toLowerCase()) !== -1) {
                const el = target.closest('[data-label]')
                if (el) {
                    const val = el.getAttribute('data-label')
                    if (val) updateSearchParams = val
                }
            }

            if (updateSearchParams && updateSearchParams !== '') {
                updateSearchParams = updateSearchParams.replace(/\s/g, '');
                let newurl = `${currentUrl.pathname}?content=${updateSearchParams}`
                localStorage.setItem('prevent-tab-scroll', 'true')
                history.push(newurl);
                event.preventDefault();
                return false;
            }

            // Loop parents till we find the link
            let link = target;
            while (link.parentElement && link.tagName.toLowerCase() !== 'a') link = link.parentElement;

            //if automatic scroll to top on navigation is disabled
            const preventNavigationScroll = link.getAttribute('data-prevent-page-onload-scroll')
            if (preventNavigationScroll && preventNavigationScroll !== '') {
                localStorage.setItem('prevent-nav-scroll', 'true')
            }

            // API links don't need navigation
            if (link.getAttribute('data-api-link') === 'true') return false;

            // File download link doesn't need navigation
            if (link.hasAttribute('download')) return false;
            //  Opening to new window doens't need navigation
            if (link.getAttribute('target') === '_blank') return false;

            // If we have a link, see if we need to navigate
            if (link.tagName.toLowerCase() === 'a') {
                const targetUrl: URL = new URL((link as HTMLAnchorElement).href, currentUrl);
                targetUrlString = targetUrl.href;
                // Handle Blob urls
                if (targetUrl.protocol === 'blob:') return false;

                // Handle excluded routes
                let isExcluded = false;
                config.excludedRoutes?.forEach(route => {
                    if (targetUrlString.indexOf(route.path) !== -1) {
                        isExcluded = true;
                    }
                });
                
                if (isExcluded) return false;

                // Handle document types
                const doctypes = ['pdf', 'xlsx', 'docx', 'zip'];
                let href = targetUrl.pathname;
                if (href !== '') {
                    if (href[href.length - 1] === '/') href = href.substring(0, href.length - 1)
                    const lastPath = href.substring(href.lastIndexOf('/') + 1);
                    if (lastPath.indexOf('.') !== -1) {
                        const afterlastdot = lastPath.substring(lastPath.lastIndexOf('.') + 1)
                        if (doctypes.indexOf(afterlastdot.toLowerCase()) !== -1) return false;
                    }
                }

                // Only act if we remain on the same domain
                if (!isBlob && targetUrl.origin === currentUrl.origin) {
                    newPath = targetUrl.pathname + targetUrl.search
                }
            }

            // Do not navigate to the same page
            if (newPath === location.pathname) {
                if (config.enableDebug) console.info('ElementNavigation: Ignoring navigation to same path');
                event.preventDefault();
                return false;
            }

            // Navigate to the new path
            if (newPath) {
                if (config.basePath && newPath.substr(0, config.basePath.length) === config.basePath) {
                    newPath = newPath.substr(config.basePath.length);
                    if (newPath.substr(0, 1) !== '/') newPath = '/' + newPath; // Ensure we've an absolute path
                }
                const newUrlLang = newPath.indexOf('/fi/') !== -1 || newPath.endsWith('/fi') ? 'fi' : 'en'
                //if link is changing language --> hard load the link
                if (targetUrlString !== '' && currentUrlLang !== newUrlLang) window.location.href = targetUrlString
                else history.push(newPath);

                event.preventDefault();

                return false;
            }
        }

        try {
            if (localStorage.getItem('prevent-tab-scroll') !== 'true' && localStorage.getItem('prevent-nav-scroll') !== 'true') { 
                if(navigator.userAgent.indexOf('iPhone') !== -1) {
                    window.scrollTo(0, 1);
                } else window.scrollTo(0, 0);
                
            } else {
                localStorage.removeItem('prevent-tab-scroll')
                localStorage.removeItem('prevent-nav-scroll')
            }
        } catch (e) {
            if (epi.isDebugActive()) console.warn('ElementNavigation: Failed to scroll to top');
        }
        document.addEventListener('click', onWindowClick);
        return () => {
            if (epi.isDebugActive()) console.info('ElementNavigation: Removing catch-all click handling for navigation');
            document.removeEventListener('click', onWindowClick);
        }
    });

    return props.children as React.ReactElement;
}
ElementNavigation.displayName = "Optimizely CMS: Generic click event handler";

export type RoutedContentProps = SwitchProps & {
    keyPrefix?: string,
    config?: IRouteConfig,
    basePath?: string
}

export const RoutedContent: React.FunctionComponent<RoutedContentProps> = (props) => {
    const ctx = useEpiserver();
    const switchProps: SwitchProps = { location: props.location }
    return <Switch {...switchProps}>
        {props.children}
        {(props.config || []).map((item, idx) => createRouteNode(item, props.basePath, `${props.keyPrefix}-route-${idx}`, ctx))}
    </Switch>
}
RoutedContent.displayName = "Optimizely CMS: Route container";

function createRouteNode(route: IRouteConfigItem, basePath = "", key?: string, ctx?: IEpiserverContext): React.ReactElement<RouteProps> {

    let createdRoute: string = basePath ? (basePath.substr(-1) === "/" ? basePath.substr(0, -1) : basePath) : "";
    createdRoute = createdRoute + "/" + (route.path ? (route.path.substr(0, 1) === "/" ? route.path.substr(1) : route.path) : "")

    if (ctx?.isDebugActive()) console.log('Generating Route Virtual DOM Node', createdRoute, route, key);

    const newRouteProps: RouteProps = {
        children: route.children,
        exact: route.exact,
        location: route.location,
        path: createdRoute,
        sensitive: route.sensitive,
        strict: route.strict,
        render: route.render ? (props: RouteComponentProps) => { return route.render ? route.render({ ...props, routes: route.routes, path: route.path }) : <div /> } : undefined,
        component: route.component ? (props: RouteComponentProps) => { const RouteComponent = route.component || 'div'; return <RouteComponent {...props} routes={route.routes} path={route.path} /> } : undefined
    }
    return <Route {...newRouteProps} key={key} />
}